import { DSPopoverModule } from '@akso/shuffle-ds';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { FeatureToggleService } from 'src/app/core/services/feature/feature-toggle.service';
import { ProjectDto } from 'src/app/core/state/project.store';
import {
  ViewMode,
  WorksheetService,
} from 'src/app/deliverable-profiles/review-worksheet/shared/services/worksheet/worksheet.service';
import { AppAvatarComponent } from 'src/app/shared/avatar/avatar.component';
import { IconSizeDirective } from 'src/app/shared/directives/icon-size.directive';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { PageTitleService } from 'src/app/shared/services/page-title.service';
import { ProjectService } from '../../core/services/project.service';
import { AvatarPopoverComponent } from '../avatar-popover/avatar-popover.component';
import { ProjectSelectorComponent } from '../project-selector/project-selector.component';
import { DeliverableSearchComponent } from './deliverable-search/deliverable-search.component';
import { DevModeComponent } from './dev-mode/dev-mode.component';
import { HelpCentreComponent } from './help-centre/help-centre.component';
import { NotificationMenuComponent } from './notification-menu/notification-menu.component';
@Component({
  selector: 'app-top-navigation',
  templateUrl: './top-navigation.component.html',
  styleUrl: './top-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DSPopoverModule,
    ProjectSelectorComponent,
    DeliverableSearchComponent,
    HelpCentreComponent,
    AvatarPopoverComponent,
    DevModeComponent,
    NotificationMenuComponent,
    MatIconModule,
    MatMenuModule,
    IconSizeDirective,
    MatTabGroup,
    MatTabsModule,
    MatButtonModule,
    MatTooltipModule,
    AppAvatarComponent,
  ],
})
export class TopNavigationComponent {
  readonly #router = inject(Router);
  readonly #navigationService = inject(NavigationService);
  readonly #projectService = inject(ProjectService);
  readonly #featureToggleService = inject(FeatureToggleService);
  readonly #worksheetService = inject(WorksheetService);
  readonly #window = inject(WINDOW);
  readonly pageTitle = inject(PageTitleService).title;
  readonly pageTitleText = computed(() => {
    const currentTitle = this.pageTitle()?.text ?? '';

    if (!currentTitle || currentTitle.includes('(')) {
      return currentTitle;
    }

    const profileKind = getProfileKindFromUrl(this.#router.url);

    return profileKind ? `${currentTitle} (${profileKind})` : currentTitle;
  });
  readonly #isWorksheetViewModeOptionsEnabled =
    this.#featureToggleService.getAsSignal('WorksheetViewModeOptions');
  readonly showProjectSelector = computed(() => getShouldShowProjectSelector(this.projects(), this.#router));
  readonly hasPageTitleDropdown = computed(() => {
    return this.pageTitle()?.hasDropdown === true;
  });
  readonly showInlinePageTitleTabs = computed(
    () => this.hasPageTitleDropdown() && this.pageTitle()?.showInlineTabs === true,
  );
  readonly showViewModeToggle = computed(
    () => this.pageTitle()?.showViewModeToggle === true && this.#isWorksheetViewModeOptionsEnabled(),
  );
  readonly viewMode = this.#worksheetService.viewMode;

  readonly projects = computed(() => {
    const isLoading = this.#projectService.projectStore.isLoading();
    const projects = this.#projectService.projectStore.projects();
    return isLoading ? [] : projects;
  });
  readonly currentProject = computed(() => {
    const projects = this.projects();
    const currentProject = this.#projectService.projectStore.currentProject();
    return projects.find(p => p.id === currentProject?.id);
  });

  onDeliverableSelect(deliverableId: number) {
    if (deliverableId) {
      this.#navigationService.navigateToDeliverableProfile(deliverableId);
    }
  }

  onSelectedProject(project: ProjectDto | undefined): void {
    this.#navigationService.navigateToDashboard(project?.id);
  }

  onViewModeChange(viewMode: ViewMode): void {
    this.#worksheetService.setViewMode(viewMode);
  }

  reloadWebsite(): void {
    this.#window.location = '/';
  }
}

function getProfileKindFromUrl(url: string): string | null {
  const normalizedUrl = url.toLowerCase();

  if (!normalizedUrl.includes('/profile/')) {
    return null;
  }

  if (normalizedUrl.includes('document')) {
    return 'Document';
  }

  if (normalizedUrl.includes('interface-index')) {
    return 'Interface Index';
  }

  if (normalizedUrl.includes('tag-index')) {
    return 'Tag Index';
  }

  if (
    normalizedUrl.includes('model3d') ||
    normalizedUrl.includes('model-3d') ||
    normalizedUrl.includes('3d-model')
  ) {
    return '3D Model';
  }

  return null;
}

function getShouldShowProjectSelector(projects: ProjectDto[], router: Router): boolean {
  const urlSegments = getUrlSegments(router);
  const isErrorPage = urlSegments.length > 2 && urlSegments[1] === 'error';
  const isUserSetupOrNewProject =
    urlSegments.length > 1 && (urlSegments[1] === 'usersetup' || urlSegments[1] === 'projectlist');
  return !(isErrorPage || isUserSetupOrNewProject || !projects.length || getIsRootUrl(router));
}

function getIsRootUrl(router: Router): boolean {
  return getUrlSegments(router).every(item => item === '');
}
function getUrlSegments(router: Router): string[] {
  return router.url.split('/');
}
